"use client";

/**
 * FilePreview — the unified preview engine for every "render this file at
 * size X" surface in the app. Three call sites:
 *
 *   - FinderTile           → size="tile"   (64×72 inset inside a 72×80 box)
 *   - FileQuickLookCard    → size="card"   (110×130 paper-shape chip)
 *   - FilePreviewCanvas    → size="panel"  (fills the Computer right pane)
 *
 * Panel-mode dispatches IMMEDIATELY to <FilePanelRenderer> (rich rendering:
 * MarkdownRenderer for .md, CsvTable for .csv, Office Online iframe for
 * .xlsx/.docx/.pptx, full-file <pre> for code/text, iframe src={url} +
 * sandbox="allow-scripts" for .html, <iframe src={url}> for .pdf, native
 * <img>/<video controls>/<audio controls>, Preview/Source tabs for text-
 * fetchable categories). See FilePanelRenderer.tsx.
 *
 * Tile/card-mode dispatches off the filename extension via `categorise()`:
 *
 *   image  → <img> with onError → fallback to stamped paper
 *   video  → <video muted playsInline> + play-glyph overlay
 *   audio  → music-note glyph on orange gradient
 *   html   → iframe srcDoc at "real" 540×700 canvas, CSS-scaled down so
 *            the page lays out correctly and the user sees a literal
 *            tiny rendering. sandbox="" for max isolation.
 *   pdf    → <embed> using #toolbar=0&navpanes=0&scrollbar=0; same scale
 *            treatment; lazy-mounts on IntersectionObserver to avoid
 *            fetching every PDF on the page upfront.
 *   text   → <pre> with the cleaned snippet at real font size, scaled.
 *   binary → DocPreviewFrame (paper-shape with folded corner + corner stamp).
 *
 * All lazy fetches (HTML iframe content, text snippet) gate on the
 * `usePreviewText` sentinel ref so off-screen previews don't fire
 * network requests. The PDF branch uses `useInView` directly.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  DocPreviewFrame,
  stampForFile,
} from "@/components/files-browser/finder-icons";
import { ThumbImg } from "@/components/files-browser/ThumbImg";
import { FilePanelRenderer } from "./FilePanelRenderer";
import { categorise } from "./file-category";
import { PlayGlyphIcon } from "@/components/icons/PlayGlyphIcon";
import { MusicNoteIcon } from "@/components/icons/MusicNoteIcon";

export type FilePreviewSize = "card" | "panel";

interface Props {
  /** Filename with extension, e.g. "haiku.md" or "report.pdf". Used for
   *  extension dispatch and for the corner stamp label. */
  filename: string;
  /** Signed URL for content fetches (HTML / PDF / text / image / video).
   *  Pass undefined to render the binary fallback. */
  url?: string;
  /** Precomputed video poster frame (a stored JPEG, never the video itself).
   *  Tile/card video only — when set, renders a plain <img> instead of a
   *  <video> element; undefined falls back to today's browser-decoded first
   *  frame. No effect on any other kind. */
  posterUrl?: string;
  /** Surface this preview is mounted on. Drives the outer dimensions and
   *  the default for `showStamp`. */
  size: FilePreviewSize;
  /** Override the corner-stamp visibility. Defaults: true for tile/card,
   *  false for panel (panels show filename + type in the toolbar). */
  showStamp?: boolean;
  className?: string;
}

export function FilePreview(props: Props) {
  // Panel mode delegates to a dedicated renderer with its own hooks. We
  // route at the top-level boundary so FilePanelChipPreview's hooks never
  // run for panel-mode (and vice versa) — keeping each branch's Rules of
  // Hooks invariant clean.
  if (props.size === "panel") {
    return <FilePanelRenderer filename={props.filename} url={props.url} />;
  }
  return <FileChipPreview {...props} />;
}

// ── Tile/card renderer ──────────────────────────────────────────────────
//
// Split out as its own component so that FilePreview's panel/non-panel
// switch can return either tree without violating the Rules of Hooks.
// All hooks (useState, usePreviewText, useInView) live here and always run
// in the same order.

function FileChipPreview({ filename, url, posterUrl, size }: Props) {
  // A media file whose bytes 404/expire falls back to the paper preview
  // instead of a broken-image square.
  const [mediaBroken, setMediaBroken] = useState(false);
  const kind = categorise(filename);
  const stamp = stampForFile(filename);
  const fills = (kind === "image" || kind === "video") && !!url && !mediaBroken;

  if (fills && kind === "image") {
    return (
      <div data-preview-kind="image" className="contents">
        <ThumbImg
          src={url}
          alt=""
          onError={() => setMediaBroken(true)}
          className="absolute inset-0 h-full w-full object-contain rounded-4 shadow-file-quick-look-card"
          shimmerClassName="rounded-4 shadow-file-quick-look-card"
        />
      </div>
    );
  }

  if (fills && kind === "video") {
    return (
      <div data-preview-kind="video" className="contents">
        {posterUrl ? (
          <ThumbImg
            src={posterUrl}
            alt=""
            onError={() => setMediaBroken(true)}
            className="absolute inset-0 h-full w-full object-contain rounded-4 shadow-file-quick-look-card"
            shimmerClassName="rounded-4 shadow-file-quick-look-card"
          />
        ) : (
          <video
            src={url}
            muted
            playsInline
            preload="metadata"
            onError={() => setMediaBroken(true)}
            className="absolute inset-0 w-full h-full object-contain rounded-4 shadow-file-quick-look-card"
          />
        )}
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-black/35 backdrop-blur-[2px]">
            <PlayGlyphIcon />
          </span>
        </span>
      </div>
    );
  }

  // --- Branch: AUDIO ---
  if (kind === "audio") {
    return (
      <div
        data-preview-kind="audio"
        className={cn(
          "relative w-full h-full rounded-4 shadow-file-quick-look-card bg-gradient-to-b from-orange-50 to-white",
          "flex justify-center items-center"
        )}
      >
        <MusicNoteIcon className="w-1/2 text-orange-500" />
      </div>
    );
  }

  // tile uses the folded-corner paper shape with the inline stamp drawn
  // inside the page rectangle. (Panel is handled by the early return above.)
  return (
    <div
      data-preview-kind={
        kind === "html"
          ? "html"
          : kind === "pdf"
            ? "pdf"
            : kind === "text"
              ? "text"
              : "binary"
      }
      className="contents"
    >
      <DocPreviewFrame
        stamp={stamp.label}
        stampHtmlBg={stamp.htmlBg}
        className="relative w-16 h-20 drop-shadow-file-preview-3"
      />
    </div>
  );
}
